'use client';

import { useMemo } from 'react';

import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { commitmentsQuery } from '../queries';
import { sortCommitmentsByPriority } from '../utils/commitments';
import { useSupporterQuery } from './base';

export const useCommitmentSortByPriority = () => {
  // @ts-expect-error, TODO: tighten up useSupporterQuery so these types are inferred
  const { data: commitments = [] }: { data?: GraphQLCommitment[] } =
    useSupporterQuery([commitmentsQuery]);

  const groupedCommitments = useMemo(() => {
    return sortCommitmentsByPriority(commitments);
  }, [commitments]);

  return groupedCommitments;
};

/**
 * This checks and returns commitments with amount due that aren't on a payment plan
 * @returns { hasPaymentDue, totalAmountDue, commitmentsWithAmountDue }
 */
export const useCommitmentsWithAmountDue = (): {
  hasPaymentDue: boolean;
  totalAmountDue: number;
  commitmentsWithAmountDue: GraphQLCommitment[];
} => {
  // @ts-expect-error, TODO: tighten up useSupporterQuery so these types are inferred
  const { data: commitments = [] }: { data?: GraphQLCommitment[] } =
    useSupporterQuery([commitmentsQuery]);

  const { commitmentsWithAmountDue, totalAmountDue } = useMemo(() => {
    const knownCommitments = sortCommitmentsByPriority(
      // TODO: this logic should be replaced by data coming back soon
      commitments?.filter(
        (c) =>
          c.status === 'Active' &&
          (!c.isRecurring || !c.paymentMethodId) &&
          c.amountDue &&
          c.amountDue > 0,
      ),
    );

    return {
      commitmentsWithAmountDue: knownCommitments,
      // TODO: this should be replaced by data coming back
      totalAmountDue: knownCommitments.reduce(
        (sum, c) => sum + (c.amountDue || 0),
        0,
      ),
    };
  }, [commitments.length]);

  return {
    hasPaymentDue:
      commitmentsWithAmountDue && commitmentsWithAmountDue?.length > 0,
    totalAmountDue,
    commitmentsWithAmountDue,
  };
};

/**
 * This method checks and returns commitments both on a payment plan or not with missed payments
 * @returns { hasMissedPayment, totalAmountMissed, missedPaymentCommitments }
 */
export const useCommitmentsWithMissingPayments = (): {
  hasMissedPayment: boolean;
  totalAmountMissed: number;
  missedPaymentCommitments: GraphQLCommitment[];
} => {
  // @ts-expect-error, TODO: tighten up useSupporterQuery so these types are inferred
  const { data: commitments = [] }: { data?: GraphQLCommitment[] } =
    useSupporterQuery([commitmentsQuery]);

  const { missedPaymentCommitments, totalAmountMissed } = useMemo(() => {
    const currentCommitments = sortCommitmentsByPriority(
      // TODO: this logic should be replaced by data coming back soon
      commitments?.filter((c) => c.status === 'Active' && c.hasMissedPayment),
    );

    return {
      missedPaymentCommitments: currentCommitments,
      totalAmountMissed: currentCommitments.reduce(
        (sum, c) => sum + (c.amountDue || 0),
        0,
      ),
    };
  }, [commitments.length]);

  return {
    hasMissedPayment:
      (missedPaymentCommitments && missedPaymentCommitments?.length > 0) ||
      false,
    totalAmountMissed,
    missedPaymentCommitments,
  };
};
